import { z } from 'zod'

export const ContactField = z.enum([
  'name',
  'firstName',
  'lastName',
  'email',
  'phone',
  'mailingAddress',
  'mailingCity',
  'mailingState',
  'mailingZipCode',
  'displayCity',
  'displayState',
])
export const contactFields = ContactField.options
export const PersonContact = z.strictObject({
  id: z.guid(),
  name: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  mailingAddress: z.string().nullable(),
  mailingCity: z.string().nullable(),
  mailingState: z.string().nullable(),
  mailingZipCode: z.string().nullable(),
  displayCity: z.string().nullable(),
  displayState: z.string().nullable(),
  ahstcMember: z.boolean(),
})
export const ContactChanges = PersonContact.omit({ id: true, ahstcMember: true })
  .partial()
  .extend({
    name: z.string().trim().min(1).optional(),
    email: z.string().trim().email().nullable().optional(),
  })
  .refine(data => Object.keys(data).length > 0, 'At least one contact field is required')
export const isPersonMatchTerm = (value: string) =>
  (value.match(/[\p{L}\p{N}]/gu)?.length ?? 0) >= 3
export const PersonMatchQuery = z.strictObject({
  q: z.string().trim().max(100).refine(isPersonMatchTerm, 'Enter at least three letters or digits'),
})
export const PersonMatch = PersonContact.pick({
  id: true,
  name: true,
  email: true,
  phone: true,
  displayCity: true,
  displayState: true,
}).extend({ matchProof: z.string(), ahstcMember: z.boolean().optional() })
export const PersonMatches = z.object({
  people: z.array(PersonMatch).max(20),
  refineSearch: z.boolean(),
})
export const StaffPersonMatches = z.object({
  people: z
    .array(
      PersonMatch.extend({ credentialNumber: z.string().nullable(), ahstcMember: z.boolean() })
    )
    .max(20),
  refineSearch: z.boolean(),
})
export const SelectPersonRequest = z.strictObject({ matchProof: z.string().min(1) })
export const PersonContactResponse = z.object({
  person: PersonContact,
  reviewToken: z.string(),
  selectionProof: z.string().optional(),
})
export const UpdateContactRequest = z.strictObject({
  changes: ContactChanges,
  reviewToken: z.string().min(1),
  selectionProof: z.string().optional(),
})
export const CreateShowEntrantRequest = z.union([
  z.strictObject({ personId: z.guid(), selectionProof: z.string().optional() }),
  z.strictObject({
    newPerson: PersonContact.omit({ id: true, ahstcMember: true })
      .partial()
      .extend({
        name: z.string().trim().min(1),
        email: z.string().trim().email().nullable().optional(),
      }),
    affiliation: z.string().optional(),
    entrantCode: z
      .string()
      .regex(/^[A-Za-z0-9-]{1,6}$/)
      .optional(),
  }),
])
export const ReleaseContactProtectionRequest = z.strictObject({
  fields: z.array(ContactField).min(1),
  reviewToken: z.string().min(1),
})
export type TPersonMatch = z.infer<typeof PersonMatch>
export type TContactField = z.infer<typeof ContactField>
export type TPersonMatches = z.infer<typeof PersonMatches>
export type TStaffPersonMatch = z.infer<typeof StaffPersonMatches>['people'][number]
export type TStaffPersonMatches = z.infer<typeof StaffPersonMatches>
export type TPersonContact = z.infer<typeof PersonContact>
export type TPersonContactResponse = z.infer<typeof PersonContactResponse>
export type TUpdateContactRequest = z.infer<typeof UpdateContactRequest>
export type TCreateShowEntrantRequest = z.infer<typeof CreateShowEntrantRequest>
